<template>
    <Dialog :visible="visible" @update:visible="v => $emit('close', v)" modal
        header="Gateway ↔ Provedor" class="w-11 lg:w-9">
        <p class="text-sm text-gray-400 mt-0 mb-3">
            Descubra o provedor por trás de um CNPJ de beneficiário (gateway/cobrador).
            O vínculo do provedor vem do <b>contrato</b> — não muda quando o gateway troca.
        </p>

        <form @submit.prevent="buscar" class="flex gap-2 mb-3">
            <InputText v-model="q" class="w-full"
                placeholder="Buscar por CNPJ do gateway ou nome do provedor..." />
            <Button type="submit" label="Buscar" icon="pi pi-search" severity="primary" :loading="isLoading" />
        </form>

        <DataTable :value="lista" :loading="isLoading" size="small" stripedRows showGridlines
            dataKey="id" v-model:expandedRows="expandedRows" scrollable scrollHeight="55vh">
            <Column expander style="width: 3rem" />
            <Column field="nome" header="Provedor" />
            <Column header="CNPJ do provedor">
                <template #body="{ data }">{{ fmtCnpj(data.documento) || '—' }}</template>
            </Column>
            <Column header="CNPJ(s) do gateway">
                <template #body="{ data }">
                    <div class="flex flex-wrap gap-1">
                        <Tag v-for="g in data.gateways" :key="g.beneficiario_cnpj"
                            :value="fmtCnpj(g.beneficiario_cnpj)"
                            :severity="g.is_proprio ? 'success' : 'warn'"
                            :title="g.is_proprio ? 'Mesmo CNPJ do provedor' : 'Gateway/terceiro'" />
                        <span v-if="!data.gateways || !data.gateways.length" class="text-gray-500">—</span>
                    </div>
                </template>
            </Column>
            <Column header="Nº de contas" style="width: 120px">
                <template #body="{ data }">{{ data.num_contas }}</template>
            </Column>
            <Column header="Último vencimento" style="width: 160px">
                <template #body="{ data }">{{ fmtData(data.ultimo_vencimento) }}</template>
            </Column>

            <template #expansion="{ data }">
                <div class="p-2">
                    <DataTable :value="data.gateways" size="small" showGridlines>
                        <Column header="CNPJ do gateway">
                            <template #body="{ data: g }">
                                <Tag :value="fmtCnpj(g.beneficiario_cnpj)"
                                    :severity="g.is_proprio ? 'success' : 'warn'" />
                            </template>
                        </Column>
                        <Column header="Nome no boleto">
                            <template #body="{ data: g }">{{ g.beneficiario_nome || '—' }}</template>
                        </Column>
                        <Column header="Cadastrado (remessa)">
                            <template #body="{ data: g }">
                                <span v-if="g.nome_cadastrado" class="inline-flex align-items-center gap-2">
                                    <Tag value="cadastrado" severity="success" />
                                    {{ g.nome_cadastrado }}
                                </span>
                                <span v-else class="text-gray-500">—</span>
                            </template>
                        </Column>
                        <Column header="Nº de contas" style="width: 110px">
                            <template #body="{ data: g }">{{ g.num_contas }}</template>
                        </Column>
                        <Column header="Primeiro visto" style="width: 140px">
                            <template #body="{ data: g }">{{ fmtData(g.primeiro_visto) }}</template>
                        </Column>
                        <Column header="Último visto" style="width: 140px">
                            <template #body="{ data: g }">{{ fmtData(g.ultimo_visto) }}</template>
                        </Column>
                        <Column style="width: 110px">
                            <template #body="{ data: g }">
                                <Button icon="pi pi-pencil" size="small" text severity="info"
                                    v-tooltip.top="g.nome_cadastrado ? 'Editar cadastro' : 'Cadastrar beneficiário'"
                                    @click="openEdit(g)" />
                                <Button v-if="g.nome_cadastrado" icon="pi pi-trash" size="small" text severity="danger"
                                    v-tooltip.top="'Remover cadastro'" @click="removerCadastro(g)" />
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </template>

            <template #empty>
                <div class="text-center text-gray-500 py-3">
                    Nenhum beneficiário capturado ainda. Assim que boletos forem lidos
                    (upload ou IA), os gateways aparecem aqui por provedor.
                </div>
            </template>
        </DataTable>

        <div class="flex justify-content-between align-items-center mt-2">
            <span class="text-sm text-gray-500">{{ pagination.total }} provedores</span>
            <Paginator :first="pagination.first" :rows="pagination.perPage"
                :totalRecords="pagination.total" @page="onPage" />
        </div>

        <!-- Cadastro/edição do beneficiário (CNPJ → nome) vinculado ao provedor. -->
        <Dialog :visible="edit.visible" @update:visible="v => { if (!v && !edit.saving) edit.visible = false }"
            modal header="Cadastrar beneficiário" class="w-11 lg:w-4">
            <p class="text-sm text-gray-400 mt-0 mb-3">
                CNPJ <b>{{ fmtCnpj(edit.cnpj) }}</b> — este nome vira o usado na remessa (J-52) deste provedor
                e silencia o aviso de divergência nas próximas.
            </p>
            <div class="field">
                <label class="block mb-1 text-sm">Nome do beneficiário</label>
                <InputText v-model="edit.nome" class="w-full" />
            </div>
            <template #footer>
                <Button label="Cancelar" text severity="secondary" :disabled="edit.saving" @click="edit.visible = false" />
                <Button label="Salvar" icon="pi pi-check" :loading="edit.saving"
                    :disabled="!edit.nome || !edit.nome.trim()" @click="salvarEdit" />
            </template>
        </Dialog>
    </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Paginator from 'primevue/paginator';
import Tag from 'primevue/tag';

import Provedor from '@/service/provedor.js';
import Mask from '@/utils/Mask.js';

export default {
    components: { Dialog, DataTable, Column, InputText, Button, Paginator, Tag },
    props: {
        visible: { type: Boolean, default: false },
    },
    emits: ['close'],
    data() {
        return {
            q: '',
            lista: [],
            expandedRows: {},
            isLoading: false,
            pagination: { page: 0, perPage: 0, total: 0, first: 0 },
            edit: { visible: false, provedor_id: null, cnpj: '', nome: '', saving: false },
        };
    },
    watch: {
        visible(v) {
            if (v) {
                this.q = '';
                this.pagination.page = 0;
                this.loadData();
            }
        },
    },
    methods: {
        fmtCnpj(doc) {
            return doc ? Mask.cnpj(doc) : '';
        },
        fmtData(v) {
            if (!v) return '—';
            const d = String(v).substring(0, 10).split('-');
            return d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : v;
        },
        buscar() {
            this.pagination.page = 0;
            this.loadData();
        },
        loadData() {
            const params = [`page=${1 + this.pagination.page}`];
            if (this.q) params.push(`q=${encodeURIComponent(this.q)}`);

            this.isLoading = true;
            Provedor.beneficiarioMapa(params.join('&'))
                .then(response => {
                    this.lista = response.data.data;
                    this.expandedRows = {};
                    this.pagination.perPage = response.data.per_page;
                    this.pagination.total = response.data.total;
                    this.pagination.first = response.data.per_page * this.pagination.page;
                })
                .catch(error => {
                    const msg = error.response?.data?.msg || error.message;
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: msg, life: 5000 });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        onPage(event) {
            this.pagination.page = event.page;
            this.loadData();
        },
        openEdit(g) {
            this.edit = {
                visible: true,
                provedor_id: g.provedor_id,
                cnpj: g.beneficiario_cnpj,
                nome: g.nome_cadastrado || g.beneficiario_nome || '',
                saving: false,
            };
        },
        salvarEdit() {
            const nome = (this.edit.nome || '').trim();
            if (!nome) return;

            this.edit.saving = true;
            Provedor.beneficiarioSalvar({ provedor_id: this.edit.provedor_id, cnpj: this.edit.cnpj, nome })
                .then(() => {
                    this.$toast.add({ severity: 'success', summary: 'Pronto', detail: 'Beneficiário cadastrado.', life: 3000 });
                    this.edit.visible = false;
                    this.loadData();
                })
                .catch(error => {
                    const msg = error.response?.data?.msg || error.message;
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: msg, life: 5000 });
                })
                .finally(() => {
                    this.edit.saving = false;
                });
        },
        removerCadastro(g) {
            this.$confirm.require({
                header: 'Remover cadastro',
                message: `Remover o beneficiário cadastrado para o CNPJ ${this.fmtCnpj(g.beneficiario_cnpj)}? `
                    + 'Volta a valer o nome do boleto e o aviso de divergência.',
                icon: 'pi pi-trash',
                acceptLabel: 'Remover',
                acceptClass: 'p-button-danger',
                rejectLabel: 'Cancelar',
                accept: () => {
                    Provedor.beneficiarioRemover({ provedor_id: g.provedor_id, cnpj: g.beneficiario_cnpj })
                        .then(() => {
                            this.$toast.add({ severity: 'success', summary: 'Pronto', detail: 'Cadastro removido.', life: 3000 });
                            this.loadData();
                        })
                        .catch(error => {
                            const msg = error.response?.data?.msg || error.message;
                            this.$toast.add({ severity: 'error', summary: 'Ops!', detail: msg, life: 5000 });
                        });
                },
            });
        },
    },
};
</script>
